import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, useTheme, IconButton } from "@mui/material";
import { keyframes } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LifecycleRow from "./LifecycleRow";
import LifecycleRowSkeleton from "./LifecycleRowSkeleton";
import { fetchLifecycleStagesWithCounts } from "../../../../redux/Slice/Lifecycle/lifecycleStageSlice";

const pageSlideIn = keyframes`
  from { opacity: 0; transform: translateX(var(--slide-from, 16px)); }
  to { opacity: 1; transform: translateX(0); }
`;

// Workspace-specific: stages + per-stage counts come straight from the
// LifecycleStage model / lifecycleStageSlice (same source Settings > Lifecycle
// uses), scoped server-side to req.workspaceId off the JWT — no client-side
// filtering needed. Both fetches are already cheap (10min-cached stage list,
// single aggregation for counts — see getLifecycleStageCounts.service.js).
function LifecyclePipeline({ stagesPerPage = 4 }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [direction, setDirection] = useState(1);

  const lifecycleStages = useSelector((state) => state.lifecycleStage.stages);
  const counts = useSelector((state) => state.lifecycleStage.counts);
  const unassignedCounts = useSelector((state) => state.lifecycleStage.unassignedCounts);
  // Safe to use `loading` directly here — fetchLifecycleStagesWithCounts uses
  // createCachedThunk (returns the SAME in-flight promise to duplicate callers,
  // e.g. StrictMode's double mount-effect), not a debounce-reject-with-fake-rejection
  // like inboxRecentSlice.js, so there's no equivalent bug where `loading` gets reset
  // while a real fetch is still running.
  const stagesLoading = useSelector((state) => state.lifecycleStage.loading);
  const currentWorkspace = useSelector((state) => state.workspace?.currentWorkspace);
  const authWorkspaceId = useSelector((state) => state.auth?.user?.workspaceId);
  const workspaceId = currentWorkspace?._id || authWorkspaceId;

  useEffect(() => {
    if (!workspaceId) return;
    dispatch(fetchLifecycleStagesWithCounts());
  }, [dispatch, workspaceId]);

  // Reset to page 1 when the workspace (and therefore the stage list) changes,
  // so a stale page index from a longer pipeline doesn't strand the user past
  // the end of a shorter one.
  useEffect(() => {
    setPage(1);
  }, [workspaceId]);

  const stages = useMemo(
    () =>
      lifecycleStages.map((stage) => ({
        stageId: stage._id,
        icon: stage.icon,
        label: stage.name,
        color: stage.color || "#64748b",
        count: counts[stage._id] || 0,
        unassigned: unassignedCounts[stage._id] || 0,
      })),
    [lifecycleStages, counts, unassignedCounts]
  );

  const totalPages = Math.max(1, Math.ceil(stages.length / stagesPerPage));
  const visibleStages = stages.slice((page - 1) * stagesPerPage, page * stagesPerPage);

  const goToPage = (next) => {
    setDirection(next > page ? 1 : -1);
    setPage(next);
  };

  return (
    <Box
      sx={{
        maxWidth: 460,
        ml: "auto",
        flex: 1,
        backgroundColor: theme.palette.background.default,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "24px",
        p: 2,
        height: 500,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
        <Typography sx={{ fontWeight: 600, fontSize: "1rem", pl: 0.75 }}>Lifecycle Pipeline</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            size="small"
            disabled={page === 1}
            onClick={() => goToPage(Math.max(1, page - 1))}
            sx={{ width: 26, height: 26, border: `1px solid ${theme.palette.divider}` }}
          >
            <ChevronLeftIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 500, color: "text.secondary" }}>
            Page {page} of {totalPages}
          </Typography>
          <IconButton
            size="small"
            disabled={page === totalPages}
            onClick={() => goToPage(Math.min(totalPages, page + 1))}
            sx={{ width: 26, height: 26, border: `1px solid ${theme.palette.divider}` }}
          >
            <ChevronRightIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Box>
      <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary", mb: 3, pl: 0.75 }}>
        Track your contacts across the lifecycle stages
      </Typography>
      <Box
        key={page}
        sx={{
          flex: 1,
          minHeight: 0,
          pb: 0.5,
          "--slide-from": direction === 1 ? "16px" : "-16px",
          animation: `${pageSlideIn} 0.3s cubic-bezier(0.4, 0, 0.2, 1)`,
        }}
      >
        {stagesLoading && stages.length === 0 ? (
          <LifecycleRowSkeleton count={stagesPerPage} />
        ) : (
          <>
            {visibleStages.map((stage) => (
              <LifecycleRow key={stage.stageId} stage={stage} />
            ))}
            {stages.length === 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%" }}>
                <Box sx={{ mb: 1.5, color: (theme) => theme.palette.text.secondary, display: "flex" }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none">
                    <g clipPath="url(#clip0_3261_13808)">
                      <path d="M18.9 7.93003L17.92 8.16003C17.22 8.32003 16.67 8.87003 16.51 9.57003L16.28 10.55C16.26 10.65 16.11 10.65 16.08 10.55L15.85 9.57003C15.69 8.87003 15.14 8.32003 14.44 8.16003L13.46 7.93003C13.36 7.91003 13.36 7.76003 13.46 7.73003L14.44 7.50003C15.14 7.34003 15.69 6.79003 15.85 6.09003L16.08 5.11003C16.1 5.01003 16.25 5.01003 16.28 5.11003L16.51 6.09003C16.67 6.79003 17.22 7.34003 17.92 7.50003L18.9 7.73003C19 7.75003 19 7.90003 18.9 7.93003Z" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" />
                      <path d="M10.61 20.5L11.94 19.17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M20.83 13.51C21.46 13.89 21.87 14.55 21.94 15.28C21.94 16.92 19.34 18.31 15.66 18.89" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M3.05 13.51C2.42 13.89 2.01 14.55 1.94 15.28C1.94 17.43 6.42 19.17 11.94 19.17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M15.61 13.32C14.69 14.39 13.34 15 11.93 15C10.52 15 9.18003 14.39 8.25003 13.32C6.63003 11.46 6.65003 8.69 8.27003 6.83L10.64 4.09C10.97 3.71 11.44 3.5 11.94 3.5C12.44 3.5 12.92 3.72 13.24 4.09" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </g>
                    <defs>
                      <clipPath id="clip0_3261_13808">
                        <rect width="24" height="24" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </Box>
                <Typography variant="h6" color="text.secondary">
                  No lifecycle stages configured yet.
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}

export default LifecyclePipeline;
